import { useState } from 'react';
import { X } from 'lucide-react';
import { NotificationSection, Severity } from '../../types';
import { cn } from '../../utils';
import { Button, ButtonProps } from '../Button';
import { ComingSoon } from '../ComingSoon';
import { NotificationToastIcon } from './NotificationToastIcon';
import { formatTime, formatDate } from './NotificationToast.utils';

export interface CustomToastProps {
  variant?: Severity;
  title?: string;
  description?: React.ReactNode;
  taskId?: string;
  taggedBy?: string;
  onClose?: () => void;
  createdAt?: string;
  seenAt?: string | null;
  isExtension?: boolean;
  fullWidth?: boolean;
  section?: string;
  action?: NotificationToastAction;
  closeAlign?: 'center' | 'top';
}

export interface NotificationToastAction {
  label?: string;
  onClick?: (taskId: string) => void;
  isLoading?: boolean;
  icon?: React.ReactNode;
  ActionButtonProps?: ButtonProps;
  disabled?: boolean;
  tooltip?: string;
}

export const NotificationToast = ({
  variant = 'info',
  title,
  taskId,
  description,
  taggedBy,
  onClose,
  createdAt,
  seenAt,
  isExtension,
  action,
  section,
  fullWidth = false,
  closeAlign = 'center',
}: CustomToastProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const formatedDate =
    section === NotificationSection.Earlier
      ? formatDate(createdAt)
      : formatTime(createdAt);

  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  return (
    <div
      onClick={toggleExpanded}
      className={cn(
        'border-border border-l-secondary flex flex-col rounded-lg border border-l-6 bg-white p-4 shadow-lg',
        {
          'border-primary border-2': seenAt === null,
          'max-w-115.5': !isExtension,
          'w-full min-[601px]:w-95': isExtension,
          'w-full min-[601px]:w-full': fullWidth,
        }
      )}
    >
      <div className="flex items-start">
        <div className="flex-1">
          <div className="flex gap-1.5">
            <div className="size-4 shrink-0">
              <NotificationToastIcon severity={variant} />
            </div>
            {title && (
              <p className="text-primary text-xs leading-4 font-semibold">
                {title}
              </p>
            )}
          </div>
          {description && (
            <div
              className={cn(
                'text-muted-foreground mt-2 text-xs leading-4 transition-all duration-200',
                {
                  'line-clamp-3': !isExpanded,
                }
              )}
            >
              {description}
              {taggedBy && (
                <span className="text-primary font-medium">{` ${taggedBy}`}</span>
              )}
            </div>
          )}
        </div>

        {createdAt ? (
          <span className="ml-2 text-xs text-gray-500">{formatedDate}</span>
        ) : (
          <button
            type="button"
            className={cn(
              'text-primary ml-auto shrink-0 cursor-pointer items-center',
              {
                'self-center': closeAlign === 'center',
                'self-start': closeAlign === 'top',
              }
            )}
            onClick={e => {
              e.stopPropagation();
              onClose?.();
            }}
          >
            <X className="ml-1 size-3.5" />
          </button>
        )}
      </div>
      {action && (
        <div className="mt-2 flex justify-end">
          <ComingSoon tooltipText={action.tooltip}>
            <Button
              variant={action.ActionButtonProps?.variant || 'outline'}
              size="sm"
              disabled={action.isLoading || action.disabled}
              onClick={() => action.onClick?.(taskId || '')}
              className="flex items-center gap-2"
              {...action.ActionButtonProps}
            >
              {action.icon && <span className="shrink-0">{action.icon}</span>}
              {action.label}
            </Button>
          </ComingSoon>
        </div>
      )}
    </div>
  );
};
